import { useCallback, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'

import { type AccessorialRate } from '../../domain/Accessorial'
import { submitAccessorialRates } from '../../use-cases/submitAccessorialRates'
import { useRfpContext } from '../contexts/RfpContext'

type SubmitState = {
  lastSubmittedRate: AccessorialRate
  submitAccessorialRatesStatus: RequestStatus
}

export function useSubmitAccessorialRates(initialRate: AccessorialRate) {
  const [{ lastSubmittedRate, submitAccessorialRatesStatus }, setSubmitState] =
    useState<SubmitState>({
      lastSubmittedRate: initialRate,
      submitAccessorialRatesStatus: RequestStatus.Initial,
    })

  const { setLastSaved } = useRfpContext()

  const submit = useCallback(
    async (
      carrierInvite: string,
      accessorialRate: AccessorialRate,
      controller: AbortController
    ) => {
      const isSameId = lastSubmittedRate.id === accessorialRate.id
      const isSameRateValue = Number(lastSubmittedRate.rate) === Number(accessorialRate.rate)
      const isSameNotesValue = lastSubmittedRate.notes === accessorialRate.notes
      const cannotSubmit = isSameId && isSameRateValue && isSameNotesValue

      if (cannotSubmit) {
        return
      }

      setSubmitState(prev => ({
        ...prev,
        submitAccessorialRatesStatus: RequestStatus.Pending,
      }))
      try {
        const result = await submitAccessorialRates(carrierInvite, accessorialRate, controller)
        setLastSaved()
        setSubmitState({
          lastSubmittedRate: result,
          submitAccessorialRatesStatus: RequestStatus.Done,
        })
      } catch (err) {
        if (!controller.signal.aborted) {
          setSubmitState(prev => ({
            ...prev,
            submitAccessorialRatesStatus: RequestStatus.Error,
          }))
        }
      }
    },

    [lastSubmittedRate, setLastSaved]
  )

  return {
    lastSubmittedRate,
    submitAccessorialRates: submit,
    submitAccessorialRatesStatus,
  }
}
